import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';
import { Box, Button, Grid, CircularProgress, Divider, Switch, Alert, Tooltip, Typography, IconButton } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import SyncPricesDialog, { SyncPricesMode } from 'components/dialogs/SyncPricesDialog';
import BookingRequestFiles from 'components/BookingRequestFiles';
import SyncIcon from '@mui/icons-material/Sync';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import WarningIcon from '@mui/icons-material/Warning';
import PlusIcon from '@mui/icons-material/Add';

import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { yupResolver } from '@hookform/resolvers/yup';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { hintValidationAlert } from 'helper/form';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputDate } from 'components/form/FormInputDate';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';

import SimpleAccordion from 'components/SimpleAccordion';
import { EditorAccordion } from 'components/template_editor/EditorAccordion';
import {
  CancellationRuleFormType,
  ContentBlockFormType,
  DownpaymentFormType,
  LineItemFormType,
  OfferVersionFormType,
  QuickPriceCalculatorContext,
  TaxesFormType,
  SeminarRoomValidation,
  validationSchemaLister,
  validationSchemaWidget,
} from 'components/template_editor/context';
import { isSeminarRoomFacility } from 'semshared/pricelist/seminarRoom';
import { HTMLOfferRenderer } from 'components/template_editor/HTMLRenderer';

import {
  OFFERVERSION_VIEW_QUERY,
  OFFERVERSION_QUICKPRICE_QUERY,
  OFFERVERSION_PRODUCTTEXT_QUERY,
  REFETCH_OFFER_QUERIES,
  EVICT_OFFER_QUERIES,
  OFFERVERSION_UPDATE_CANCELLATION_RULES_MUTATION,
  OFFERVERSION_UPDATE_DOWNPAYMENT_MUTATION,
  OFFERVERSION_UPDATE_COMMISSION_MUTATION,
  PRECALCULATE_OFFERVERSION_COMMISSION,
  OFFERVERSION_VIEW_DOWNPAYMENT_QUERY,
  OFFERVERSION_VIEW_CANCELLATION_RULES_QUERY,
} from './gql';
import {
  EOfferVersionContentBlockType,
  EContentProductDetailPosition,
  WLOfferVersionListOutput,
  EOfferRoomOccupancy,
  EOfferRoomSeating,
  ViewWlOfferVersionQuery,
  EOfferSource,
  ListTaxTypesQuery,
  ListFullTaxTypesQuery,
  EOfferVersionContentBlockFormFieldType,
  EOfferVersionContentBlockLineItemSource,
  EOfferVersionType,
  ListServiceTypesQuery,
  ListServiceTypesTextQuery,
  EContentOfferTemplateType,
  FindOfferTemplateQuery,
  ViewWlOfferVersionDownpaymentQuery,
  ViewWlOfferVersionCancellationRulesQuery,
  WLOfferVersionDiffOutputFragment,
  EOfferCommissionBase,
} from '__generated__/graphql';

import { NO_PRICE } from 'semshared/pricelist/quickprice';
import { ProductTextSelector, ProductTextSelectorType } from 'semshared/pricelist/producttextselector';
import { applyTemplateToInput, calculateTotals, mapOfferStatusToDocumentType } from 'semshared/offer/offer';
import { RedirectError } from 'pages/error';
import { FormInputCountryDropdown, FormInputDropdown, SimpleDropdown } from 'components/form/FormInputDropdown';
import { TAXTYPES_LIST_FULL_QUERY } from 'pages/prices/gql';
import Price, { formatPrice } from 'components/Price';
import { QuickPriceCalculatorLister, QuickPriceCalculatorTypeLister } from 'semshared/pricelist/quickprice_lister';
import { IQuickPriceInputWidget, QuickPriceCalculatorTypeWidget, QuickPriceCalculatorWidget } from 'semshared/pricelist/quickprice_widget';
import Percentage, { formatPercentage } from 'components/Percentage';
import SimpleTable from 'components/table/SimpleTable';
import { formatDate } from 'components/DateTime';
import { LabeledFrame } from 'components/LabeledFrame';
import { getTaxAmountByRate } from 'components/tax/TaxRenderer';
import { canChangeCancellationRules, canChangeOfferCancellationRules } from 'semshared/offer/cancellation';
import { canChangeDownpayment, canChangeOfferDownpayment } from 'semshared/offer/downpayment';
import { calculateCommissionForOffer, getDefaultCommissionRate, ICommission, ICommissionableOfferVersion, ICommissionRate } from 'semshared/offer/commission';
import { userSelector } from 'helper/security';
import { CancellationRulesDialog, hasCategorySpecificRules, splitRulesForDisplay } from './cancellationrules';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { DownpaymentDialog } from './downpayment';
import { CommissionDialog } from './commission';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { OfferDocuments } from '../../components/OfferDocuments';
import { OfferStandardDocuments } from '../../components/OfferStandardDocuments';
import { SERVICETYPES_LIST_TEXT_QUERY } from 'pages/products/gql';
import { FIND_OFFERTEMPLATE_QUERY } from 'pages/content/gql';
import { getGuestsStatistics } from './reports/utils/countHelper';

/** Count diffs vs approved; keys are `day:sku` or `day:header` for special requests without SKU. */
function buildChangedLineItemKeys(diffToApproved: WLOfferVersionDiffOutputFragment['diffToApproved']): Set<string> {
  const keys = new Set<string>();
  for (const dayDiff of diffToApproved?.diff ?? []) {
    for (const itemDiff of dayDiff.diffs) {
      if (!itemDiff.hasCountDiff) continue;
      const identity = itemDiff.sku || itemDiff.header;
      if (identity) {
        keys.add(`${dayDiff.day}:${identity}`);
      }
    }
  }
  return keys;
}

/** Highlight only real count changes — not every REQUESTED row (e.g. prior customer addons on other days). */
function isLineItemChangeRequestHighlight(
  lineItem: { day: number; sku?: string | null; header?: string | null; source: EOfferVersionContentBlockLineItemSource } | null | undefined,
  changedLineItemKeys: Set<string>,
): boolean {
  if (!lineItem) return false;
  const identity = lineItem.sku || lineItem.header;
  return !!identity && changedLineItemKeys.has(`${lineItem.day}:${identity}`);
}

interface OfferVersionProps {
  versionId: number;
  offerId: number;
  offerSource: EOfferSource;
  disabled: boolean;
  canChangeCounts: boolean;
  canChangeItems: boolean;
  canAddItems: boolean;
  canDeleteItems: boolean;
  canChangeStructure: boolean;
  canChangePrices: boolean;
  canChangeClientData: boolean;
  canSeeClientData: boolean;
  isChangeRequest: boolean;
  /** Amber row highlight during hotel CR review; independent of full CR editor mode (isChangeRequest). */
  highlightChangeRequestItems: boolean;
  diffToApproved?: WLOfferVersionDiffOutputFragment['diffToApproved'] | null;
  submitTrigger: number;
  submitProcess: boolean;
  canSwitchServiceType: boolean;
  applyOfferTemplateType?: EContentOfferTemplateType | null;
  onChangeIsValid: (isValid: boolean) => void;
  onChangeCanSaveState: (canSave: boolean) => void;
  doSubmit: (
    values: OfferVersionFormType,
    switchServiceTypeSku?: string | null,
    process?: boolean,
  ) => Promise<WLOfferVersionListOutput>;
  onSubmitted: (switchTofferVersionId: number) => void;
  onChangeFormValues?: (values: OfferVersionFormType) => void;
  /** Live SEM request period so the customer-request tab follows added or removed days. */
  onOfferPeriodChange?: (period: { startDate: string; endDate: string } | null) => void;
  guestMode?: boolean;
}

interface OfferVersionFormProps extends OfferVersionProps {
  data: NonNullable<ViewWlOfferVersionQuery['viewWlOfferVersion']>;
  downpaymentData: NonNullable<ViewWlOfferVersionDownpaymentQuery['viewWlOfferVersion']>;
  cancellationRulesData: NonNullable<ViewWlOfferVersionCancellationRulesQuery['viewWlOfferVersion']>;
  quickPriceCalculator: QuickPriceCalculatorTypeWidget | QuickPriceCalculatorTypeLister;
  taxTypes: ListFullTaxTypesQuery['listTaxTypes'];
  serviceTypes: ListServiceTypesTextQuery['listServiceTypes']['serviceTypes'];
  applyOfferTemplate?: FindOfferTemplateQuery['findOfferTemplate'];
}

/**
 * Request-tab period for a SEM offer being edited.
 * Extends the copied request when the hotel adds a later day, and follows a changed arrival date.
 */
export function resolveWidgetRequestPeriod(
  startDate: string | Date | null | undefined,
  contentBlocks: { type?: string | null; day?: number | null; lineItems?: { day?: number | null }[] | null }[] | null | undefined,
  storedStart?: string | null,
  storedEnd?: string | null,
): { startDate: string; endDate: string } | null {
  if (!startDate) return null;
  const start = moment(startDate).format('YYYY-MM-DD');
  if (start === 'Invalid date') return null;

  const offerDays = (contentBlocks ?? []).flatMap(block => {
    const fromBlock = block?.type === 'LINE_ITEMS' && typeof block.day === 'number' && block.day >= 0 ? [block.day] : [];
    const fromItems = (block?.lineItems ?? [])
      .map(item => (typeof item?.day === 'number' ? item.day : null))
      .filter((day): day is number => day != null && day >= 0);
    return [...fromBlock, ...fromItems];
  });
  const storedSpan =
    storedStart && storedEnd ? Math.max(0, moment(storedEnd).startOf('day').diff(moment(storedStart).startOf('day'), 'days')) : 0;
  const maxDay = offerDays.length > 0 ? Math.max(storedSpan, ...offerDays) : storedSpan;
  return { startDate: start, endDate: moment(start).add(maxDay, 'days').format('YYYY-MM-DD') };
}

function OfferVersionForm(props: OfferVersionFormProps) {
  const dispatch = useDispatch();
  const user = userSelector()!;

  const [newItemSource] = useState(props.data.type === EOfferVersionType.OFFER ? EOfferVersionContentBlockLineItemSource.OFFER : EOfferVersionContentBlockLineItemSource.EXTRA);

  // Use stored isPricesNet from offer version's priceList instead of looking up by date
  // (which may fail for old offers if the pricelist for that date no longer exists)
  const [isPricesNet] = useState(props.data.priceList.isPricesNet);

  const { maxGuests, maxRoomsEZ, maxRoomsDZ, startDate, endDate } = getGuestsStatistics(props.data.input, props.data.inputLister);

  const [renderCycle, setRenderCycle] = useState(0);
  const [submitTrigger, setSubmitTrigger] = useState(props.submitTrigger);
  const [suppressUnsavedPrompt, setSuppressUnsavedPrompt] = useState(false);
  const [serviceTypeId, setServiceTypeId] = useState<number | null>((props.data.offer.source === EOfferSource.WIDGET && props.data.input?.serviceType?.serviceTypeId) || null);

  const serverCancellationRules = props.cancellationRulesData?.offer?.cancellationRules ?? [];
  const [cancellationRulesBackup, setCancellationRulesBackup] = useState<CancellationRuleFormType[]>(serverCancellationRules);

  const hotelCancellationRules: CancellationRuleFormType[] = useMemo(() => {
    const hotelRules = (props.data.hotel as { cancellationRules?: Array<{ id?: number; daysToEvent?: number; rate?: number; minOvernight?: number | null; maxOvernight?: number | null; minTotalGuests?: number | null; maxTotalGuests?: number | null }> })?.cancellationRules ?? [];
    return hotelRules.map(r => ({
      id: r.id ?? null,
      daysToEvent: r.daysToEvent ?? 0,
      rate: r.rate ?? 0,
      minOvernight: r.minOvernight ?? null,
      maxOvernight: r.maxOvernight ?? null,
      minTotalGuests: r.minTotalGuests ?? null,
      maxTotalGuests: r.maxTotalGuests ?? null,
    }));
  }, [props.data.hotel]);

  const effectiveCancellationRules: CancellationRuleFormType[] = useMemo(() => {
    if (cancellationRulesBackup.length > 0) return cancellationRulesBackup;
    const mainRules = props.data.offer.cancellationRules ?? [];
    if (mainRules.length > 0) {
      return mainRules.map(r => ({
        id: r.id,
        daysToEvent: r.daysToEvent,
        rate: r.rate ?? 0,
        minOvernight: null,
        maxOvernight: null,
        minTotalGuests: null,
        maxTotalGuests: null,
      }));
    }
    return hotelCancellationRules;
  }, [cancellationRulesBackup, props.data.offer.cancellationRules, hotelCancellationRules]);

  /** Matches server/PDF: offer field, else hotel waiver, else first hotel rule with tolerance (legacy data). */
  const effectiveCancellationToleranceRate = useMemo(() => {
    const fromDedicatedQuery = props.cancellationRulesData.offer.cancellationToleranceRate;
    const fromOffer =
      fromDedicatedQuery != null && fromDedicatedQuery !== undefined
        ? fromDedicatedQuery
        : props.data.offer.cancellationToleranceRate;
    if (fromOffer != null && fromOffer !== undefined) return fromOffer;
    const hotel = props.data.hotel as {
      cancellationToleranceRate?: number | null;
      cancellationRules?: Array<{ toleranceRate?: number | null }>;
    };
    if (hotel.cancellationToleranceRate != null && hotel.cancellationToleranceRate !== undefined) {
      return hotel.cancellationToleranceRate;
    }
    const fromRule = hotel.cancellationRules?.find(r => r.toleranceRate != null && r.toleranceRate > 0)?.toleranceRate;
    return fromRule ?? null;
  }, [props.cancellationRulesData.offer.cancellationToleranceRate, props.data.offer.cancellationToleranceRate, props.data.hotel]);

  const toFormSchema = (obj: NonNullable<ViewWlOfferVersionQuery['viewWlOfferVersion']>): OfferVersionFormType => {
    // LISTER: day headers use form startDate; prefer inputLister so Tag N matches the request period.
    // Bare YYYY-MM-DD calendar-date strings (SM-2778).
    const startDate =
      obj.offer.source === EOfferSource.LISTER && obj.inputLister?.startDate ? obj.inputLister.startDate : obj.startDate;
    let result: OfferVersionFormType = {
      id: obj.id,
      title: obj.title,
      filename: obj.filename,
      version: obj.version,
      offerDate: obj.offerDate,
      startDate,
      additionalInfo1: obj.additionalInfo1 || '',
      additionalInfo2: obj.additionalInfo2 || '',
      additionalInfo3: obj.additionalInfo3 || '',
      personalMessage: obj.personalMessage || '',
      agreedCancellationWaiveNet: obj.agreedCancellationWaiveNet,
      manualCancellationWaiveNet: obj.manualCancellationWaiveNet,
      contentBlocks: obj.contentBlocks.map(cb => ({
        ...cb,
        formFields: cb.formFields.map(ff => ({
          name: ff.name,
          type: ff.type,
          label: ff.label,
          sequence: ff.sequence,
          required: ff.required,
          value: ff.value,
          id: ff.id,
          placeholder: ff.placeholder,
        })),
        day: cb.lineItems[0] ? cb.lineItems[0].day : 0,
        cancellationRule: cb.cancellationRule
          ? {
            daysToEvent: cb.cancellationRule.daysToEvent,
            rate: cb.cancellationRule.rate,
          }
          : undefined,
        lineItems: (cb.lineItems ?? []).map(li => ({
          ...li,
          priceItem: li.priceItem,
          priceNet: li.priceNet,
          priceGross: li.priceGross,
          source: li.source,
          invoice: li.invoice ? {
            commission: li.invoice.commission,
          } : null,
          components: (li.components ?? []).map(c => ({
            taxTypeId: c.type.id,
            name: c.type.name,
            lookupCode: c.type.lookupCode,
            price: c.price,
          })),
          taxes: (li.taxes ?? []).map(t => ({
            taxTypeId: t.type.id,
            taxRateId: t.rate.id,
            name: t.type.name,
            lookupCode: t.type.lookupCode,
            rate: t.rate.rate,
            price: t.price,
          })),
        })),
      })) as any,
      totalPriceNet: obj.totalPriceNet,
      totalPriceGross: obj.totalPriceGross,
      totalTax: (obj.totalPriceNet === NO_PRICE || obj.totalPriceGross === NO_PRICE) ? NO_PRICE : obj.totalPriceGross - obj.totalPriceNet,
      totalTaxes: obj.taxes.map(t => ({
        taxTypeId: t.type.id,
        taxRateId: t.rate.id,
        name: t.type.name,
        lookupCode: t.type.lookupCode,
        rate: t.rate.rate,
        price: t.price,
      })),
      pageOptions: {
        marginTop: obj.marginTop || 0,
        marginBottom: obj.marginBottom || 0,
        marginLeft: obj.marginLeft || 0,
        marginRight: obj.marginRight || 0,
        cssStyles: obj.cssStyles,
        headerTemplate: obj.headerTemplate,
        footerTemplate: obj.footerTemplate,
      },
      includeEmptyLineItems: obj.includeEmptyLineItems,
      client: props.canSeeClientData
        ? {
          ...obj.client,
        }
        : null
    };
    if (props.applyOfferTemplate) {
      // Preserve the reservation's startDate (and derived endDate) when applying a cancellation template
      // so "Tag 1" matches the original reservation date instead of being reset to today.
      // Bare YYYY-MM-DD calendar-date strings (SM-2778).
      const preservedStartDate = result.startDate || moment().format('YYYY-MM-DD');
      const maxLineItemDay = Math.max(
        0,
        ...result.contentBlocks.flatMap(cb => (cb.lineItems || []).map(li => li.day || 0)),
      );
      const preservedEndDate = moment(preservedStartDate).add(maxLineItemDay, 'days').format('YYYY-MM-DD');
      result = applyTemplateToInput(props.data.hotel.spaceId, props.applyOfferTemplate, {
        ...result,
        documentType: 'CINVOICE',
        refCode: props.data.offer.refCode,
        date: new Date(),
        startDate: preservedStartDate,
        endDate: preservedEndDate,
        expirationDate: new Date(),
        hotel: props.data.hotel,
        client: props.data.client,
        cancellationRules: effectiveCancellationRules,
      });
    }
    return result;
  };

  const seminarRoomValidationRef = useRef<SeminarRoomValidation>({
    enforce: false,
    isSeminarRoom: () => false,
    requestAt: () => null,
  });
  seminarRoomValidationRef.current = {
    enforce: !props.disabled,
    isSeminarRoom: sku => isSeminarRoomFacility(props.quickPriceCalculator.allFacilitiesDb?.find(facility => facility.sku === sku)),
    requestAt: (day, seminarIndex) => props.data.inputLister?.days?.find(entry => entry.day === day)?.occupancy?.[seminarIndex] ?? null,
  };

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(props.offerSource === EOfferSource.WIDGET ? validationSchemaWidget : validationSchemaLister) as any,
    context: { client: useApolloClient(), seminarRoomValidationRef },
    defaultValues: toFormSchema(props.data),
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors: validationErrors, isValid, isDirty, isValidating, isSubmitting },
  } = form;

  const {
    fields: contentBlocksFields,
    remove: contentBlocksRemove,
    replace: contentBlocksReplace,
  } = useFieldArray({
    control,
    name: 'contentBlocks',
  });

  const handleDocumentChange = useCallback(() => {
    const timestamp = Date.now();
    setValue('_documentChangeTimestamp', timestamp, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false
    });
  }, [setValue, form.formState.isDirty]);


  const [advanced, setAdvanced] = useState(false);
  const [showChangeRequest, setShowChangeRequest] = useState(props.isChangeRequest ? true : false);
  const changedLineItemKeys = useMemo(
    () => buildChangedLineItemKeys(props.diffToApproved),
    [props.diffToApproved],
  );
  const [showHtml, setShowHtml] = useState(props.data.offer.source === EOfferSource.WIDGET);
  const fullWidth = !showHtml;

  const [commissionRate, setCommissionRate] = useState<ICommissionRate>(() => {
    // If offer already has commission values, use them
    if (
      props.data.offer.commissionBase &&
      props.data.offer.commissionPercentage !== null &&
      props.data.offer.commissionPercentage !== undefined
    ) {
      return {
        commissionBase: props.data.offer.commissionBase,
        commissionPercentage: props.data.offer.commissionPercentage,
      };
    }

    // Otherwise calculate from current rules
    return getDefaultCommissionRate(
      props.data.hotel,
      props.data.offer.publicCompany,
      props.data.offerDate
    );
  });

  const [commission, setCommission] = useState<ICommission>();
  const [updateCommission] = useMutation(OFFERVERSION_UPDATE_COMMISSION_MUTATION);
  const [isCommissionDialogOpen, setIsCommissionDialogOpen] = useState(false);
  // Backoffice only, Epilo offers, and only while the offer editor is editable
  const canEditCommission = user.isSeminargo && !props.disabled && props.data.offer.source === EOfferSource.LISTER;

  const _calculateCommission = useCallback(() => {
    const values = getValues();
    const version: ICommissionableOfferVersion = {
      offer: {
        source: props.data.offer.source,
        process: props.data.offer.process,
      },
      agreedCancellationWaiveNet: values.agreedCancellationWaiveNet,
      manualCancellationWaiveNet: values.manualCancellationWaiveNet,
      contentBlocks: (values.contentBlocks ?? []).filter(cb => (cb.type === 'LINE_ITEMS' || cb.type === 'CANCELLATION_ITEMS') && (cb.lineItems || []).length > 0).map(cb => ({
        type: cb.type,
        lineItems: (cb.lineItems ?? []).filter(Boolean).map(li => ({
          source: li.source,
          sku: li.sku,
          count: li.count,
          priceItem: li.priceItem,
          priceNet: li.priceNet,
          priceGross: li.priceGross,
          invoice: li.invoice ? {
            commission: li.invoice.commission,
          } : null,
          components: (li.components ?? []).map(c => ({
            price: c.price,
            taxType: {
              lookupCode: props.taxTypes.find(tt => tt.id === c.taxTypeId)?.lookupCode ?? null,
            }
          })),
        })),
      })),
    };
    const commission = calculateCommissionForOffer(version, commissionRate, props.quickPriceCalculator);
    setCommission(commission);
  }, [getValues, props.taxTypes, commissionRate, props.quickPriceCalculator]);

  const [updateCancellationRules] = useMutation(OFFERVERSION_UPDATE_CANCELLATION_RULES_MUTATION);
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] = useState(false);

  // Keep backup in sync with server data when dialog is closed (e.g. after refetch following save)
  useEffect(() => {
    if (!isCancellationDialogOpen) {
      setCancellationRulesBackup(serverCancellationRules);
    }
  }, [isCancellationDialogOpen, serverCancellationRules]);

  // Modal mode: show Übernachtungsgäste + Seminargäste sections only when hotel has category ranges (Übernachtungsgäste/Seminargäste von-bis)
  const hotelHasCategoryCancellation = useMemo(() => {
    return hasCategorySpecificRules(hotelCancellationRules);
  }, [hotelCancellationRules]);

  const [canEditCancellationRules] = useState(user.isSeminargo ||
    (props.data.offer.source === EOfferSource.LISTER && props.data.offer.publicCompany && props.data.inputLister &&
      canChangeCancellationRules(
        props.data.hotel,
        props.data.offer.publicCompany,
        props.data.inputLister,
        props.data.offerDate
      ) && canChangeOfferCancellationRules(props.data.offer.status, props.data.status)));

  const [updateDownpayment] = useMutation(OFFERVERSION_UPDATE_DOWNPAYMENT_MUTATION);
  const [isDownpaymentDialogOpen, setIsDownpaymentDialogOpen] = useState(false);
  const [downpaymentBackup, setDownpaymentBackup] = useState<DownpaymentFormType | undefined>(props.downpaymentData.offer.downpaymentAmount ? props.downpaymentData.offer as any : undefined);

  // Service type change confirmation dialog state
  const [isServiceTypeChangeDialogOpen, setIsServiceTypeChangeDialogOpen] = useState(false);
  const [pendingServiceTypeId, setPendingServiceTypeId] = useState<number | null>(null);

  const [canEditDownpayment] = useState(user.isSeminargo ||
    (props.data.offer.source === EOfferSource.LISTER && props.data.offer.publicCompany && props.data.inputLister &&
      canChangeDownpayment(
        props.data.hotel,
        props.data.offer.publicCompany,
        props.data.inputLister
      ) && canChangeOfferDownpayment(props.data.offer.status, props.data.status)));

  useEffect(() => {
    if (props.disabled) return;
    if (submitTrigger !== props.submitTrigger) {
      debouncedOnChangeFormValues.flush();
      handleSubmit(onSubmit())();
      setSubmitTrigger(props.submitTrigger);
    }
  }, [props.submitTrigger]);

  useEffect(() => {
    if (props.disabled) return;
    props.onChangeIsValid(isValid);
    props.onChangeCanSaveState(isValid && !isSubmitting && !isValidating);
  }, [isSubmitting, isValidating, isValid]);

  // Calculate commission on initial load
  useEffect(() => {
    _calculateCommission();
  }, [_calculateCommission]);

  const publishWidgetRequestPeriod = useCallback(
    (values: { startDate?: string | Date | null; contentBlocks?: { type?: string | null; day?: number | null; lineItems?: { day?: number | null }[] | null }[] | null }) => {
      if (!props.onOfferPeriodChange) return;
      if (props.disabled || props.offerSource !== EOfferSource.WIDGET) {
        props.onOfferPeriodChange(null);
        return;
      }
      props.onOfferPeriodChange(
        resolveWidgetRequestPeriod(values.startDate, values.contentBlocks, props.data.input?.startDate, props.data.input?.endDate),
      );
    },
    [props.onOfferPeriodChange, props.disabled, props.offerSource, props.data.input?.startDate, props.data.input?.endDate],
  );

  // Überwache Änderungen und rufe Callback auf (debounced)
  const debouncedOnChangeFormValues = useCallback(
    _.debounce((value: OfferVersionFormType) => {
      if (props.onChangeFormValues) {
        props.onChangeFormValues(value);
      }
    }, 500),
    [props.onChangeFormValues],
  );

  useEffect(() => {
    if (props.disabled || (!props.onChangeFormValues && !props.onOfferPeriodChange)) return;

    const subscription = watch(value => {
      const formValues = value as OfferVersionFormType;
      if (props.onChangeFormValues) {
        debouncedOnChangeFormValues(formValues);
      }
      publishWidgetRequestPeriod(formValues);
    });

    return () => {
      subscription.unsubscribe();
      debouncedOnChangeFormValues.cancel();
    };
  }, [watch, props.disabled, props.onChangeFormValues, debouncedOnChangeFormValues, publishWidgetRequestPeriod]);

  useEffect(() => {
    // When props.data changes (e.g. offer is supplemented),
    // reset the form with the new data. This ensures that
    // react-hook-form's 'isDirty' flag is correctly initialized
    // to false until the user actually makes a change.
    // Skip during intentional submit/approve navigation to avoid a race where
    // field-array indices are stale while Draggable children still render.
    if (suppressUnsavedPrompt) return;
    const nextValues = toFormSchema(props.data);
    reset(nextValues);
    publishWidgetRequestPeriod(nextValues);
  }, [props.data, reset, suppressUnsavedPrompt, publishWidgetRequestPeriod]);

  useEffect(() => {
    if (suppressUnsavedPrompt) return;
    const nextValues = toFormSchema(props.data);
    reset(nextValues);
    publishWidgetRequestPeriod(nextValues);
  }, [props.disabled, props.data, reset, suppressUnsavedPrompt, publishWidgetRequestPeriod]);

  const onSubmit = () => async (values: OfferVersionFormType) => {
    try {
      const serviceType = props.serviceTypes.find(st => st.id === serviceTypeId);
      const switchServiceTypeSku = (props.canSwitchServiceType && serviceType && serviceType.sku !== props.data.input?.serviceType?.sku) ? serviceType.sku : undefined;

      // Suppress the unsaved-changes prompt for intentional navigation after save
      setSuppressUnsavedPrompt(true);
      const res = await props.doSubmit(values, switchServiceTypeSku, props.submitProcess);
      // Approving navigates away immediately; resetting from the mutation response can crash
      // when multi-component special-request line items are present (stale/partial payload).
      if (!props.submitProcess) {
        reset(toFormSchema(res));
      }
      // Defer navigation to next tick to allow form state to settle
      setTimeout(() => props.onSubmitted(res.id), 0);
    } catch (err) {
      dispatchException(dispatch, err);
      // Re-enable prompt if navigation will not happen due to error
      setSuppressUnsavedPrompt(false);
    }
  };

  useEffect(() => {
    if (props.disabled) return;
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (!showHtml) return;

    const subscription = watch(() => {
      setRenderCycle(prev => prev + 1);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [showHtml, watch]);

  const onLineItemSKUAdded = useCallback((cbIndex: number, sku: string) => {
    const contentBlock = getValues(`contentBlocks.${cbIndex}`);
    const lineItemDate = moment(getValues('startDate')).add(contentBlock.day!, 'days').toDate();

    try {
      const priceParams = {
        date: lineItemDate,
        itemCount: 1 as const,
        guestCount: maxGuests || 1,
        priceListId: props.data.priceList.id,
        isPremium: props.data.offer.publicCompany ? props.data.offer.publicCompany.isPremium : false,
        publicCompanyId: props.data.offer.publicCompany ? props.data.offer.publicCompany.id : undefined,
      };
      const pricedSku =
        typeof props.quickPriceCalculator.resolvePricedProductSKU === 'function'
          ? props.quickPriceCalculator.resolvePricedProductSKU(sku, priceParams)
          : sku;

      const header = props.quickPriceCalculator.productTextSelector!.getProductText({
        sku,
        hotelId: props.data.hotel.id,
        position: EContentProductDetailPosition.HEADER,
        language: props.data.offer.language,
      });
      const details = props.quickPriceCalculator.productTextSelector!.getProductText({
        sku,
        hotelId: props.data.hotel.id,
        position: EContentProductDetailPosition.DETAILS,
        language: props.data.offer.language,
      });

      const calcLineItem = props.quickPriceCalculator.getSKULineItem(pricedSku, {
        date: lineItemDate,
        itemCount: 1,
        guestCount: priceParams.guestCount,
        priceListId: props.data.priceList.id,
        isPremium: priceParams.isPremium,
        publicCompanyId: priceParams.publicCompanyId,
      }, null, { allowNoPrices: true });
      if (calcLineItem) {
        const selectedProduct = props.quickPriceCalculator.allProductsDb?.find(p => p.sku === sku);
        return {
          sku,
          count: calcLineItem.count,
          priceItem: calcLineItem.priceItem,
          priceGross: calcLineItem.priceGross,
          priceNet: calcLineItem.priceNet,
          source: newItemSource,
          day: contentBlock.day!,
          header: header || selectedProduct?.name || calcLineItem.name,
          details: details || '',
          sequence: 1000,
          components: calcLineItem.components.map(c => ({
            taxTypeId: c.type.id,
            name: c.type.name,
            price: c.price,
          })),
          taxes: calcLineItem.taxes.map(t => ({
            taxTypeId: t.type.id,
            taxRateId: t.rate.id,
            name: t.type.name,
            rate: t.rate.rate,
            price: t.price,
          })),
        };
      }
    } catch (err) {
      dispatchException(dispatch, err);
    }
  }, [getValues, props.data.hotel.id, props.data.offer.language, props.data.offer.publicCompany, props.data.priceList.id, props.quickPriceCalculator, newItemSource, dispatch, maxGuests]);
  const onLineItemFreeAdded = useCallback((cbIndex: number, taxTypeId: number | number[]) => {
    const contentBlock = getValues(`contentBlocks.${cbIndex}`);

    try {
      const taxTypeIds = Array.isArray(taxTypeId) ? taxTypeId : [taxTypeId];

      if (taxTypeIds.length === 1) {
        // Single tax type - use existing getFreeStyleLineItem logic
        const calcLineItem = props.quickPriceCalculator.getFreeStyleLineItem(1, 0, isPricesNet, taxTypeIds[0]);
        return {
          count: 1,
          priceItem: 0,
          priceGross: 0,
          priceNet: 0,
          source: newItemSource,
          day: contentBlock.day!,
          header: '',
          details: '',
          sequence: 1000,
          components: calcLineItem!.components.map(c => ({
            taxTypeId: c.type.id,
            name: c.type.name,
            price: c.price,
          })),
          taxes: calcLineItem!.taxes.map(t => ({
            taxTypeId: t.type.id,
            taxRateId: t.rate.id,
            name: t.type.name,
            rate: t.rate.rate,
            price: t.price,
          })),
        };
      }

      // Multiple tax types - create multi-component line item (special request / "Sonstiges")
      const lineItemDate = moment(getValues('startDate')).add(contentBlock.day!, 'days').toDate();
      const components = taxTypeIds.map(ttId => {
        const taxType = props.taxTypes.find(tt => tt.id === ttId);
        return {
          taxTypeId: ttId,
          name: taxType?.name ?? '',
          price: 0,
        };
      });

      const taxes = taxTypeIds.map(ttId => {
        const taxType = props.taxTypes.find(tt => tt.id === ttId);
        if (!taxType) {
          throw new Error(`Tax type ${ttId} not found`);
        }
        const taxRate = props.quickPriceCalculator.taxRateSelector?.getTaxRate({
          country: props.quickPriceCalculator.hotel!.businessCountry,
          date: lineItemDate,
          taxTypeId: ttId,
        });
        if (!taxRate) {
          throw new Error(`No tax rate found for tax type ${ttId} on ${moment(lineItemDate).format('YYYY-MM-DD')}`);
        }

        const taxTypeFromCalculator = props.quickPriceCalculator.taxRateSelector?.getTaxType(ttId);

        return {
          taxTypeId: ttId,
          taxRateId: taxRate.id,
          name: taxTypeFromCalculator?.name ?? taxType.name,
          rate: taxRate.rate,
          price: 0,
        };
      });

      return {
        count: 1,
        priceItem: 0,
        priceGross: 0,
        priceNet: 0,
        source: newItemSource,
        day: contentBlock.day!,
        header: '',
        details: '',
        sequence: 1000,
        components,
        taxes,
      };
    } catch (err) {
      dispatchException(dispatch, err);
    }
  }, [getValues, props.quickPriceCalculator, props.taxTypes, newItemSource, isPricesNet, dispatch]);

  const onTotalsChanged = useCallback(() => {
    const formValues = getValues();
    const previousTotalTaxes = formValues.totalTaxes;
    const { totalPriceNet, totalPriceGross, totalTaxes, totalTax } = calculateTotals(formValues);

    setValue('totalPriceNet', totalPriceNet);
    setValue('totalPriceGross', totalPriceGross);
    setValue('totalTax', totalTax);
    setValue(
      'totalTaxes',
      _.orderBy(totalTaxes, [t => t.name], 'asc').map(t => {
        const taxWithTypeId = t as typeof t & { taxTypeId?: number };
        const taxTypeId: number =
          taxWithTypeId.taxTypeId ??
          props.quickPriceCalculator.taxRateSelector?.taxRates.find(tr => tr.id === t.taxRateId)?.taxTypeId ??
          previousTotalTaxes.find(et => et.taxRateId === t.taxRateId)?.taxTypeId ??
          props.taxTypes.find(tt => tt.rates.some(tr => tr.id === t.taxRateId))?.id ??
          0;
        return {
          ...t,
          taxTypeId,
        } satisfies TaxesFormType;
      }),
    );
    setTimeout(() => _calculateCommission(), 0);
    trigger();
  }, [getValues, setValue, props.quickPriceCalculator, props.taxTypes, _calculateCommission, trigger]);

  const _reassignSequence = useCallback((contentBlocks: ContentBlockFormType[]) => {
    contentBlocks.forEach((cb, index) => {
      cb.sequence = index + 1;
    });
    contentBlocksReplace(contentBlocks);
  }, [contentBlocksReplace]);

  const onLineItemChanged = useCallback((cbIndex: number, liIndex: number) => {
    const contentBlock = getValues(`contentBlocks.${cbIndex}`);
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    const lineItemDate = moment(getValues('startDate')).add(contentBlock.day!, 'days').toDate();

    // Multi-component free-text line items (like "Other") need special handling
    const isMultiComponentFreeText = !lineItem.sku && lineItem.components.length > 1;

    if (isMultiComponentFreeText) {
      try {
        // Calculate net/gross totals
        let totalPriceNet = 0;
        let totalPriceGross = 0;

        lineItem.components.forEach((c: any) => {
          const taxInfo = lineItem.taxes.find((t: any) => t.taxTypeId === c.taxTypeId);
          if (taxInfo) {
            const componentPriceNet = isPricesNet ? c.price : c.price / (1 + taxInfo.rate);
            const componentPriceGross = isPricesNet ? c.price * (1 + taxInfo.rate) : c.price;

            totalPriceNet += componentPriceNet * lineItem.count;
            totalPriceGross += componentPriceGross * lineItem.count;
          }
        });

        if (lineItem.priceGross !== totalPriceGross) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.priceGross`, totalPriceGross);
        }
        if (lineItem.priceNet !== totalPriceNet) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.priceNet`, totalPriceNet);
        }

        // Recalculate tax amounts
        const newTaxes = lineItem.components.map((c: any) => {
          const taxInfo = lineItem.taxes.find((t: any) => t.taxTypeId === c.taxTypeId);
          if (!taxInfo) return null;

          const componentPriceNet = isPricesNet ? c.price : c.price / (1 + taxInfo.rate);
          const componentPriceGross = isPricesNet ? c.price * (1 + taxInfo.rate) : c.price;
          const taxPrice = (componentPriceGross - componentPriceNet) * lineItem.count;

          return {
            taxTypeId: c.taxTypeId,
            taxRateId: taxInfo.taxRateId,
            name: taxInfo.name,
            rate: taxInfo.rate,
            price: taxPrice,
          };
        }).filter((tax): tax is NonNullable<typeof tax> => tax !== null);

        const currentTaxes = lineItem.taxes.map((t: any) => ({
          taxTypeId: t.taxTypeId,
          taxRateId: t.taxRateId,
          name: t.name,
          rate: t.rate,
          price: t.price,
        }));

        if (!_.isEqual(currentTaxes, newTaxes)) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.taxes`, newTaxes);
        }

        trigger();
        onTotalsChanged();
        return;
      } catch (err) {
        dispatchException(dispatch, err);
        return;
      }
    }

    // Standard single-component line items
    try {
      let calcLineItem = null;

      if (lineItem.sku) {
        try {
          calcLineItem = props.quickPriceCalculator.getSKULineItem(
            lineItem.sku,
            {
              date: lineItemDate,
              itemCount: lineItem.count,
              isPremium: props.data.offer.publicCompany ? props.data.offer.publicCompany.isPremium : false,
              publicCompanyId: props.data.offer.publicCompany ? props.data.offer.publicCompany.id : undefined
            },
            {
              price: lineItem.priceItem,
              bundlePriceFromProduct: false,
              components: lineItem.components.map(c => ({
                taxTypeId: c.taxTypeId,
                price: c.price,
              })),
            },
          );
        } catch (err) {
          calcLineItem = props.quickPriceCalculator.getFreeStyleLineItem(
            lineItem.count,
            lineItem.priceItem,
            isPricesNet,
            lineItem.taxes[0].taxTypeId,
          );
        }
      } else {
        calcLineItem = props.quickPriceCalculator.getFreeStyleLineItem(
          lineItem.count,
          lineItem.priceItem,
          isPricesNet,
          lineItem.taxes[0].taxTypeId
        );
      }

      if (calcLineItem) {
        if (lineItem.priceGross !== calcLineItem.priceGross) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.priceGross`, calcLineItem.priceGross);
        }
        if (lineItem.priceNet !== calcLineItem.priceNet) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.priceNet`, calcLineItem.priceNet);
        }

        const newComponents = calcLineItem.components.map(c => ({
          taxTypeId: c.type.id,
          name: c.type.name,
          price: c.price,
        }));

        const currentComponents = lineItem.components.map(c => ({
          taxTypeId: c.taxTypeId,
          name: c.name,
          price: c.price,
        }));

        if (!_.isEqual(currentComponents, newComponents)) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.components`, newComponents);
        }

        const newTaxes = calcLineItem.taxes.map(t => ({
          taxTypeId: t.type.id,
          taxRateId: t.rate.id,
          name: t.type.name,
          rate: t.rate.rate,
          price: t.price,
        }));

        const currentTaxes = lineItem.taxes.map(t => ({
          taxTypeId: t.taxTypeId,
          taxRateId: t.taxRateId,
          name: t.name,
          rate: t.rate,
          price: t.price,
        }));

        if (!_.isEqual(currentTaxes, newTaxes)) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.taxes`, newTaxes);
        }

        trigger();
      }

      onTotalsChanged();
    } catch (err) {
      dispatchException(dispatch, err);
    }
  }, [getValues, props.quickPriceCalculator, props.data.offer.publicCompany, isPricesNet, setValue, trigger, onTotalsChanged, dispatch]);

  const onLineItemPriceComponentChanged = useCallback((cbIndex: number, liIndex: number, componentIndex: number) => {
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    const priceItem = lineItem.components.reduce((s, c) => ((s === NO_PRICE || c.price === NO_PRICE) ? NO_PRICE : s + c.price), 0);
    setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.priceItem`, priceItem);
    // Multi-component special requests skip the priceItemWatch effect, so derive totals here.
    if (!lineItem.sku && lineItem.components.length > 1) {
      onLineItemChanged(cbIndex, liIndex);
    }
    trigger();
  }, [getValues, setValue, trigger, onLineItemChanged]);

  /**
   * Handles manual edits to the priceItem field by redistributing the price difference
   * across tax components, prioritizing the highest tax rate first.
   * 
   * When a user manually changes the total price (priceItem), this function:
   * 1. Calculates the difference between the new total and current component sum
   * 2. Sorts components by tax rate (highest first)
   * 3. Distributes the difference to components, starting with the highest rate
   * 4. For additions: adds the full amount to the highest rate component
   * 5. For subtractions: removes from highest rate first, cascading to lower rates if needed
   * 6. Never reduces a component below 0
   * 7. Triggers recalculation of gross/net prices after redistribution
   * 
   * @param cbIndex - Index of the content block containing the line item
   * @param liIndex - Index of the line item within the content block
   * @param newPriceItem - The new manually-entered total price
   */
  const onLineItemPriceItemManuallyChanged = useCallback((cbIndex: number, liIndex: number, newPriceItem: number) => {
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);

    if (newPriceItem === NO_PRICE || lineItem.components.length === 0) {
      return;
    }

    const currentSum = lineItem.components.reduce((s, c) => s + (c.price === NO_PRICE ? 0 : c.price), 0);
    const difference = newPriceItem - currentSum;

    if (Math.abs(difference) < 0.01) {
      return;
    }

    // Sort by tax rate (highest first)
    const componentsWithRates = lineItem.components.map((c, idx) => {
      const taxType = props.taxTypes.find(tt => tt.id === c.taxTypeId);
      const taxRate = taxType?.rates.find(r => r.country === props.data.hotel.businessCountry);
      return { index: idx, component: c, rate: taxRate?.rate || 0 };
    }).sort((a, b) => b.rate - a.rate);

    let remaining = difference;

    for (const { index, component } of componentsWithRates) {
      const currentPrice = component.price === NO_PRICE ? 0 : component.price;

      if (Math.abs(remaining) < 0.01) {
        // Ensure untouched NO_PRICE components are initialized to 0
        if (component.price === NO_PRICE) {
          setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.components.${index}.price`, 0);
        }
        continue;
      }

      if (remaining > 0) {
        setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.components.${index}.price`, currentPrice + remaining);
        remaining = 0;
      } else {
        const canTake = Math.min(Math.abs(remaining), currentPrice);
        setValue(`contentBlocks.${cbIndex}.lineItems.${liIndex}.components.${index}.price`, currentPrice - canTake);
        remaining += canTake;
      }
    }

    setTimeout(() => {
      onLineItemPriceComponentChanged(cbIndex, liIndex, 0);
      setTimeout(() => onLineItemChanged(cbIndex, liIndex), 0);
    }, 0);
  }, [getValues, setValue, props.taxTypes, props.data.hotel.businessCountry, onLineItemPriceComponentChanged, onLineItemChanged]);

  const onLineItemCopyPrices = useCallback((cbIndex: number, liIndex: number) => {
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);

    if (lineItem.sku) {
      const allContentBlocks = getValues('contentBlocks');
      for (const [acbIndex, acb] of allContentBlocks.entries()) {
        if (acb.type !== EOfferVersionContentBlockType.LINE_ITEMS) continue;
        const cbLineItems = getValues(`contentBlocks.${acbIndex}.lineItems`);
        for (const [aliIndex, ali] of (cbLineItems || []).entries()) {
          if (cbIndex === acbIndex && aliIndex === liIndex) continue;
          if (ali.sku !== lineItem.sku) continue;

          setValue(`contentBlocks.${acbIndex}.lineItems.${aliIndex}.priceItem`, lineItem.priceItem);
          lineItem.components.forEach((c, cindex) => setValue(`contentBlocks.${acbIndex}.lineItems.${aliIndex}.components.${cindex}`, { ...c }));
          setTimeout(() => onLineItemChanged(acbIndex, aliIndex), 0);
        }
      }
    }
  }, [getValues, setValue, onLineItemChanged]);

  const shouldShowContentBlock = useCallback((cbIndex: number) => {
    if (!props.isChangeRequest) return true;
    if (!showChangeRequest) return true;

    const contentBlock = getValues(`contentBlocks.${cbIndex}`);
    if (contentBlock.type !== 'LINE_ITEMS') return false;
    return (contentBlock.lineItems || []).some(li => li && isLineItemChangeRequestHighlight(li, changedLineItemKeys));
  }, [props.isChangeRequest, showChangeRequest, getValues, changedLineItemKeys]);
  const visibleContentBlocks = useMemo(
    () => contentBlocksFields.map((cb, index) => ({ cb, index })).filter(({ index }) => shouldShowContentBlock(index)),
    [contentBlocksFields, shouldShowContentBlock],
  );
  const showLineItem = useCallback((cbIndex: number, liIndex: number) => {
    if (!props.isChangeRequest) return true;
    if (!showChangeRequest) return true;

    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    if (!lineItem) return false;
    return isLineItemChangeRequestHighlight(lineItem, changedLineItemKeys);
  }, [props.isChangeRequest, showChangeRequest, getValues, changedLineItemKeys]);
  const isLineItemHighlighted = useCallback(
    (cbIndex: number, liIndex: number) => {
      if (!props.highlightChangeRequestItems) return false;
      const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
      if (!lineItem) return false;
      return isLineItemChangeRequestHighlight(lineItem, changedLineItemKeys);
    },
    [props.highlightChangeRequestItems, getValues, changedLineItemKeys],
  );
  const canChangeLineItemCount = useCallback((cbIndex: number, liIndex: number) => {
    if (props.offerSource === EOfferSource.WIDGET) return true;
    if (user.isSeminargo) return true;
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    if (!lineItem) return false;
    if (props.isChangeRequest) {
      return lineItem.source === EOfferVersionContentBlockLineItemSource.REQUESTED;
    } else if (props.offerSource === EOfferSource.LISTER) {
      return !lineItem.id;
    } else {
      return true;
    }
  }, [props.isChangeRequest, user.isSeminargo, getValues]);
  const canChangeLineItemPrice = useCallback((cbIndex: number, liIndex: number) => {
    if (props.offerSource === EOfferSource.WIDGET) return true;
    if (user.isSeminargo) return true;
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    if (!lineItem) return false;
    if (props.isChangeRequest) {
      return lineItem.source === EOfferVersionContentBlockLineItemSource.REQUESTED;
    }
    return true;
  }, [props.isChangeRequest, user.isSeminargo, getValues]);
  const canChangeLineItemHeader = useCallback((cbIndex: number, liIndex: number) => {
    if (props.offerSource === EOfferSource.WIDGET) return true;
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    if (!lineItem) return false;
    return !lineItem.sku
    //lineItem.source === EOfferVersionContentBlockLineItemSource.EXTRA;
  }, [getValues]);
  const canDeleteLineItem = useCallback((cbIndex: number, liIndex: number) => {
    if (props.offerSource === EOfferSource.WIDGET) return true;
    const lineItem = getValues(`contentBlocks.${cbIndex}.lineItems.${liIndex}`);
    if (!lineItem) return false;
    if (!lineItem.id) return true;
    if (lineItem.source === EOfferVersionContentBlockLineItemSource.EXTRA) return true;
    return false;
  }, [getValues]);

  const onContentBlockDelete = useCallback((index: number) => {
    contentBlocksRemove(index);
    _reassignSequence(getValues('contentBlocks'));
    onTotalsChanged();
  }, [contentBlocksRemove, _reassignSequence, getValues, onTotalsChanged]);

  const [syncPricesDialogOpen, setSyncPricesDialogOpen] = useState(false);

  /**
   * Iterates all line items that have a SKU and re-fetches prices from the
   * quick price calculator.  In 'fill_empty' mode items that already carry a
   * price are skipped.
   */
  const onSyncPricesFromPricelist = useCallback(
    (mode: SyncPricesMode) => {
      const contentBlocks = getValues('contentBlocks');

      const updatedContentBlocks = contentBlocks.map((contentBlock: any, cbIndex: number) => {
        if (contentBlock.type !== 'LINE_ITEMS' && contentBlock.type !== 'CANCELLATION_ITEMS') return contentBlock;

        const updatedLineItems = (contentBlock.lineItems || []).map((lineItem: any, liIndex: number) => {
          if (!lineItem.sku) return lineItem;

          if (mode === 'fill_empty') {
            const isMultiTax = lineItem.components.length > 1;
            const isEmpty = isMultiTax
              ? lineItem.components.every((c: any) => !c.price || c.price === NO_PRICE || c.price === 0)
              : !lineItem.priceItem || lineItem.priceItem === NO_PRICE || lineItem.priceItem === 0;
            if (!isEmpty) return lineItem;
          }

          try {
            const lineItemDate = moment(getValues('startDate')).add(contentBlock.day ?? 0, 'days').toDate();
            const priceParams = {
              date: lineItemDate,
              itemCount: lineItem.count,
              guestCount: maxGuests || 1,
              priceListId: props.data.priceList.id,
              isPremium: props.data.offer.publicCompany?.isPremium ?? false,
              publicCompanyId: props.data.offer.publicCompany?.id ?? undefined,
            };
            const pricedSku =
              typeof props.quickPriceCalculator.resolvePricedProductSKU === 'function'
                ? props.quickPriceCalculator.resolvePricedProductSKU(lineItem.sku, priceParams)
                : lineItem.sku;

            const calcLineItem = props.quickPriceCalculator.getSKULineItem(
              pricedSku,
              {
                date: lineItemDate,
                itemCount: lineItem.count,
                guestCount: priceParams.guestCount,
                priceListId: props.data.priceList.id,
                isPremium: priceParams.isPremium,
                publicCompanyId: priceParams.publicCompanyId,
              },
              null,
              { allowNoPrices: true },
            );

            if (!calcLineItem) return lineItem;

            const newComponents = (calcLineItem.components ?? []).map((c: any) => ({
              taxTypeId: c.type.id,
              name: c.type.name,
              lookupCode: c.type.lookupCode,
              price: c.price,
            }));

            if (newComponents.every((c: any) => !c.price || c.price === NO_PRICE)) return lineItem;

            const newTaxes = (calcLineItem.taxes ?? []).map((t: any) => ({
              taxTypeId: t.type.id,
              taxRateId: t.rate.id,
              name: t.type.name,
              lookupCode: t.type.lookupCode,
              rate: t.rate.rate,
              price: t.price,
            }));

            // Keep selected SKU/header; only refresh prices from the priced twin (SM-2581).
            return {
              ...lineItem,
              priceItem: calcLineItem.priceItem,
              priceGross: calcLineItem.priceGross,
              priceNet: calcLineItem.priceNet,
              components: newComponents,
              taxes: newTaxes,
            };
          } catch (err) {
            dispatchException(dispatch, err);
            return lineItem;
          }
        });

        return { ...contentBlock, lineItems: updatedLineItems };
      });

      // Replace entire field array
      contentBlocksReplace(updatedContentBlocks);
      onTotalsChanged();
    },
    [getValues, contentBlocksReplace, props.quickPriceCalculator, props.data.offer.publicCompany, props.data.priceList.id, dispatch, onTotalsChanged, maxGuests],
  );

  const _priceSummary = useCallback(() => {
    const totalTaxes = watch('totalTaxes');
    const totalPriceNet = watch('totalPriceNet');
    const totalPriceGross = watch('totalPriceGross');
    const totalTax = watch('totalTax');
    const contentBlocks = watch('contentBlocks');

    const _rightAlignedCell = (content: React.ReactNode) => <div style={{ textAlign: 'right' }}>{content}</div>;

    return (
      <Grid container spacing={3}>
        {/* Cancellation info if present */}
        {watch('agreedCancellationWaiveNet') && (
          <Grid item xs={12}>
            {i18next.t('invoice-cancellation-agreedwaive')}: {formatPrice(watch('agreedCancellationWaiveNet')!, props.data.priceList.currency)}
          </Grid>
        )}
        {watch('manualCancellationWaiveNet') && (
          <Grid item xs={12}>
            {i18next.t('invoice-cancellation-manualwaive')}: {formatPrice(watch('manualCancellationWaiveNet')!, props.data.priceList.currency)}
          </Grid>
        )}

        <Grid item xs={12}>
          {totalPriceNet === NO_PRICE || totalPriceGross === NO_PRICE ? (
            <Alert severity="error" icon={<ErrorIcon />}>
              {i18next.t('offertemplate-totals-incomplete')}
            </Alert>
          ) : (
            <SimpleTable
              fullWidth={props.offerSource !== EOfferSource.LISTER}
              headers={[
                _rightAlignedCell(i18next.t('offertemplate-totals-taxrate')),
                _rightAlignedCell(i18next.t('offertemplate-totals-net')),
                _rightAlignedCell(i18next.t('offertemplate-totals-tax')),
                _rightAlignedCell(i18next.t('offertemplate-totals-gross')),
              ]}
              rows={[
                ...getTaxAmountByRate(totalTaxes, contentBlocks, isPricesNet).map((tax, index) => {
                  return [
                    _rightAlignedCell(<Percentage percentage={tax.rate} />),
                    _rightAlignedCell(<Price price={tax.netAmount} currency={props.data.priceList.currency} />),
                    _rightAlignedCell(<Price price={tax.price} currency={props.data.priceList.currency} />),
                    _rightAlignedCell(<Price price={tax.grossAmount} currency={props.data.priceList.currency} />),
                  ];
                }),
                [
                  <></>,
                  _rightAlignedCell(
                    <strong>
                      <Price price={totalPriceNet} currency={props.data.priceList.currency} />
                    </strong>,
                  ),
                  _rightAlignedCell(
                    <strong>
                      <Price price={totalTax} currency={props.data.priceList.currency} />
                    </strong>,
                  ),
                  _rightAlignedCell(
                    <strong>
                      <Price price={totalPriceGross} currency={props.data.priceList.currency} />
                    </strong>,
                  ),
                ],
              ]}
            />
          )}
        </Grid>
      </Grid>
    );
  }, [watch, props.offerSource, props.data.priceList.currency, isPricesNet]);

  const _formatCommissionInfo = useCallback(() => {
    const baseText = commissionRate.commissionBase === 'NET'
      ? i18next.t('offer-info-text-commission-net', { commissionPercentage: formatPercentage(commissionRate.commissionPercentage) })
      : i18next.t('offer-info-text-commission-gross', { commissionPercentage: formatPercentage(commissionRate.commissionPercentage) });

    if (commission) {
      return (
        <>
          {baseText}
          <br />
          <strong>{i18next.t('offer-info-commission-calculated', {
            amount: formatPrice(commission.commissionAmountNet, props.data.priceList.currency)
          })}</strong>
        </>
      );
    }

    return (
      <>
        {baseText}
      </>
    );
  }, [commissionRate, commission, props.data.priceList.currency]);

  const _infoSummary = useCallback(() => {
    return (
      <SimpleTable
        fullWidth={false}
        rows={[
          [<>
            <strong>{i18next.t('offer-info-header-commission')}</strong>
            <br />
            {_formatCommissionInfo()}
            {canEditCommission && (
              <>
                <br />
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setIsCommissionDialogOpen(true);
                }}>
                  {i18next.t('offer-info-edit-commission')}
                </a>
              </>
            )}
          </>],
          [<>
            <strong>{i18next.t('offer-info-header-cancellation')}</strong>
            <br />
            {i18next.t('offer-info-text-cancellation')}
            <br />
            {(() => {
              const { hasCategories, overnight, seminar, general } = splitRulesForDisplay(effectiveCancellationRules);
              const ruleLine = (rule: { daysToEvent: number; rate?: number | null }, key: string) => (
                <React.Fragment key={key}>
                  {i18next.t('offer-info-text-cancellation-rule', { daysToEvent: rule.daysToEvent, rate: formatPercentage(rule.rate ?? 0) })}<br />
                </React.Fragment>
              );
              if (hasCategories) {
                return <>
                  {overnight.length > 0 && (
                    <>
                      <strong>{i18next.t('hotel-cancellation-category-overnight')}</strong><br />
                      {overnight.map((rule, i) => ruleLine(rule, `o-${i}`))}
                    </>
                  )}
                  {seminar.length > 0 && (
                    <>
                      <strong>{i18next.t('hotel-cancellation-category-seminar')}</strong><br />
                      {seminar.map((rule, i) => ruleLine(rule, `s-${i}`))}
                    </>
                  )}
                </>;
              }
              return general.map((rule, index) => ruleLine(rule, `cancellation-rule-${index}`));
            })()}
            <>{i18next.t('offer-info-text-cancellation-rule-waive', { toleranceRate: formatPercentage(effectiveCancellationToleranceRate ?? 0) })}</>
            <br />
            {canEditCancellationRules && (
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setIsCancellationDialogOpen(true);
              }}>
                {i18next.t('offer-info-edit-cancellation')}
              </a>
            )}
          </>],
          ...((downpaymentBackup || canEditDownpayment) ? [[<>
            <strong>{i18next.t('offer-info-header-downpayment')}</strong>
            <br />
            {downpaymentBackup ? i18next.t('offer-info-text-downpayment-text', { downpaymentAmount: formatPrice(downpaymentBackup.downpaymentAmount, props.data.priceList.currency), downpaymentDueDate: formatDate(downpaymentBackup.downpaymentDueDate) }) : i18next.t('offer-info-text-downpayment-empty')}
            <br />
            {canEditDownpayment && (
              <a href="#" onClick={(e) => { e.preventDefault(); setIsDownpaymentDialogOpen(true); }}>
                {i18next.t('offer-info-edit-downpayment')}
              </a>
            )}
          </>]] : []),
        ]}
      />
    );
  }, [_formatCommissionInfo, canEditCommission, setIsCommissionDialogOpen, effectiveCancellationRules, effectiveCancellationToleranceRate, props.data.offer.cancellationRules, canEditCancellationRules, setIsCancellationDialogOpen, downpaymentBackup, canEditDownpayment, setIsDownpaymentDialogOpen, props.data.priceList.currency]);

  const _inputSummary = useCallback(() => {
    return (
      <SimpleTable
        fullWidth={false}
        rows={[
          [i18next.t('offer-input-header-duration'), `${formatDate(startDate)} - ${formatDate(endDate)}`],
          [i18next.t('offer-input-header-guests'), maxGuests],
          ...(maxRoomsEZ > 0 ? [[i18next.t('offer-input-header-overnightguests-ez'), maxRoomsEZ]] : []),
          ...(maxRoomsDZ > 0 ? [[i18next.t('offer-input-header-overnightguests-dz'), maxRoomsDZ / 2]] : []),
        ]}
      />
    );
  }, [startDate, endDate, maxGuests, maxRoomsEZ, maxRoomsDZ]);

  const _onServiceTypeChanged = useCallback((v: number) => {
    if (!props.canSwitchServiceType) return;

    const origInput = props.data.input!;

    const input: IQuickPriceInputWidget = {
      serviceTypeSku: props.quickPriceCalculator.allServiceTypesDb?.find(st => st.id === v)?.sku,
      prevdayGuests: origInput.prevdayGuests,
      days: origInput.days.map(d => ({
        day: d.day,
        overnightGuests: d.overnightGuests,
        totalGuests: d.totalGuests,
        addonFacilities: d.addons.filter(a => a.facility).map(f => ({
          sku: f.sku,
          count: f.count,
        })),
        addonProducts: d.addons.filter(a => a.product).map(p => ({
          sku: p.sku,
          count: p.count,
        })),
        occupancy: d.occupancy.map(o => `${o.occupancy}`),
        seating: d.occupancy.map(o => `${o.seating}`),
      })),
      endDate: origInput.endDate,
      language: origInput.language,
      startDate: origInput.startDate,
      isPremium: props.data.offer.publicCompany ? props.data.offer.publicCompany.isPremium : false,
      publicCompanyId: props.data.offer.publicCompany ? props.data.offer.publicCompany.id : undefined
    };

    const calcOutput = (props.quickPriceCalculator as QuickPriceCalculatorTypeWidget).calculatePrice(input);

    const contentBlocks = getValues('contentBlocks');

    setValue('contentBlocks', contentBlocks.map(cb => {
      if (cb.type === 'LINE_ITEMS') {
        const lineItems = calcOutput.lineItems.filter(li => li.day === cb.day);
        cb.lineItems = lineItems.map((li, liIndex) => {
          const header = props.quickPriceCalculator.productTextSelector?.getProductText({
            sku: li.sku,
            hotelId: props.data.hotel.id,
            position: EContentProductDetailPosition.HEADER,
            language: props.data.offer.language,
          });
          const details = props.quickPriceCalculator.productTextSelector?.getProductText({
            sku: li.sku,
            hotelId: props.data.hotel.id,
            position: EContentProductDetailPosition.DETAILS,
            language: props.data.offer.language,
          });
          return {
            sequence: liIndex + 1,
            day: li.day,
            header: header || li.name,
            details: details || '',
            source: EOfferVersionContentBlockLineItemSource.OFFER,
            sku: li.sku,
            count: li.count,
            priceItem: li.priceItem,
            priceGross: li.priceGross,
            priceNet: li.priceNet,
            components: li.components.map(c => ({
              taxTypeId: c.type.id,
              name: c.type.name,
              price: c.price,
            })),
            taxes: li.taxes.map(t => ({
              taxTypeId: t.type.id,
              taxRateId: t.rate.id,
              name: t.type.name,
              rate: t.rate.rate,
              price: t.price,
            })),
          };
        });
      }
      return cb;
    }));
    onTotalsChanged();
    setServiceTypeId(v);
  }, [props.canSwitchServiceType, props.data.input, props.quickPriceCalculator, props.data.offer.publicCompany, props.data.hotel.id, props.data.offer.language, getValues, setValue, onTotalsChanged, setServiceTypeId]);

  const _onServiceTypeChangeRequested = useCallback((v: number) => {
    setPendingServiceTypeId(v);
    setIsServiceTypeChangeDialogOpen(true);
  }, [setPendingServiceTypeId, setIsServiceTypeChangeDialogOpen]);

  const _onServiceTypeChangeConfirmed = useCallback(() => {
    if (pendingServiceTypeId !== null) {
      _onServiceTypeChanged(pendingServiceTypeId);
      setPendingServiceTypeId(null);
    }
    setIsServiceTypeChangeDialogOpen(false);
  }, [pendingServiceTypeId, _onServiceTypeChanged, setPendingServiceTypeId, setIsServiceTypeChangeDialogOpen]);

  // SEM inquiries can be sent with only an email; Epilo still requires both address blocks.
  const clientAddressRequired = props.offerSource !== EOfferSource.WIDGET;

  return (
    <QuickPriceCalculatorContext.Provider value={props.quickPriceCalculator}>
      <FormProvider {...form}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {showHtml && (
                <Grid item sm={6}>
                  <HTMLOfferRenderer
                    key={`HTMLRenderer_${renderCycle}`}
                    spaceId={props.data.space.id}
                    input={{
                      additionalInfo1: watch('additionalInfo1'),
                      additionalInfo2: watch('additionalInfo2'),
                      additionalInfo3: watch('additionalInfo3'),
                      documentType: mapOfferStatusToDocumentType(props.data.offer.status, props.data.status),
                      date: moment(watch('offerDate')).toDate(),
                      refCode: props.data.offer.refCode,
                      startDate: watch('startDate'),
                      endDate: (() => {
                        // Calculate endDate from line items: find highest day number
                        const contentBlocks = watch('contentBlocks');
                        const lineItems = contentBlocks?.flatMap(cb => cb.lineItems || []) || [];
                        if (lineItems.length > 0) {
                          const maxDay = Math.max(...lineItems.map(li => li.day || 0));
                          return moment(watch('startDate')).add(maxDay, 'days').format('YYYY-MM-DD');
                        }
                        // Fallback to startDate
                        return watch('startDate');
                      })(),
                      expirationDate: moment(watch('offerDate'))
                        .add(props.data.hotel.offerExpirationDays || 30, 'days')
                        .toDate(),
                      hotel: props.data.hotel,
                      serviceType: props.serviceTypes.find(st => st.id === serviceTypeId) || props.data.input?.serviceType,
                      client: props.canChangeClientData ? watch('client')! : props.data.client,
                      contentBlocks: watch('contentBlocks'),
                      cancellationRules: effectiveCancellationRules,
                      pageOptions: watch('pageOptions'),
                      includeEmptyLineItems: watch('includeEmptyLineItems'),
                      totalPriceNet: watch('totalPriceNet'),
                      totalPriceGross: watch('totalPriceGross'),
                      totalTax: watch('totalTax'),
                      totalTaxes: watch('totalTaxes'),
                    }}
                  />
                </Grid>
              )}
              <Grid item sm={showHtml ? 6 : 12}>
                <UnsavedChangesPrompt isDirty={isDirty && !suppressUnsavedPrompt} />
                <Grid container spacing={3}>
                  {props.data.offer.source === EOfferSource.WIDGET && (
                    <>
                      <Grid item xs={6}>
                        <Switch checked={showHtml} onChange={() => setShowHtml(!showHtml)} />
                        {i18next.t('offertemplate-showhtml')}
                      </Grid>
                      <Grid item xs={6}>
                        <Switch checked={advanced} onChange={() => setAdvanced(!advanced)} />
                        {i18next.t('offertemplate-advanced')}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} container justifyContent="flex-end">
                    {props.isChangeRequest && (
                      <Grid item xs={6}>
                        <Switch checked={showChangeRequest} onChange={() => setShowChangeRequest(!showChangeRequest)} />
                        {i18next.t('offertemplate-showchangerequest')}
                      </Grid>
                    )}
                    <Tooltip title={`${i18next.t('offertemplate-totals-displaytooltip')}`}>
                      <Typography variant="body2" color="textSecondary">
                        {isPricesNet ? i18next.t('offertemplate-totals-displaynet') : i18next.t('offertemplate-totals-displaygross')}
                      </Typography>
                    </Tooltip>
                  </Grid>
                  {!showChangeRequest && <>
                    <Grid item xs={12} sm={showHtml ? 12 : 4}>
                      {showHtml && (
                        <SimpleAccordion header={`${i18next.t('offertemplate-section-totals')}`} autoOpen>
                          {_priceSummary()}
                        </SimpleAccordion>
                      )}
                      {!showHtml && (
                        <LabeledFrame variant="h6" label={i18next.t('offertemplate-section-totals')}>
                          {_priceSummary()}
                        </LabeledFrame>
                      )}
                      {!props.disabled && props.canChangePrices && (
                        <>
                          <Box mt={1} />
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<SyncIcon />}
                            onClick={() => setSyncPricesDialogOpen(true)}
                          >
                            {i18next.t('sync-prices-button')}
                          </Button>
                          <SyncPricesDialog
                            open={syncPricesDialogOpen}
                            onClose={() => setSyncPricesDialogOpen(false)}
                            onConfirm={onSyncPricesFromPricelist}
                          />
                        </>
                      )}
                      {props.data.offer.source === EOfferSource.LISTER && (<>
                        <Box mt={2} />
                        <Alert severity="info">{i18next.t('offer-info-seminargo-included')}</Alert>
                      </>)}
                    </Grid>
                    {props.data.offer.source === EOfferSource.LISTER && (
                      <Grid item xs={12} sm={showHtml ? 12 : 4}>
                        {showHtml && (
                          <SimpleAccordion header={`${i18next.t('offertemplate-section-info')}`} autoOpen>
                            {_infoSummary()}
                          </SimpleAccordion>
                        )}
                        {!showHtml && (
                          <LabeledFrame variant="h6" label={i18next.t('offertemplate-section-info')}>
                            {_infoSummary()}
                          </LabeledFrame>
                        )}
                      </Grid>
                    )}
                    {props.canSwitchServiceType && (
                      <Grid item xs={12}>
                        <SimpleAccordion
                          header={`${i18next.t('offertemplate-section-servicetype')}`}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={fullWidth ? 4 : 12}>
                              <SimpleDropdown
                                name="serviceTypeId"
                                onChange={v => _onServiceTypeChangeRequested(v)}
                                options={(props.quickPriceCalculator.allServiceTypesDb || []).map(st => ({ label: st.name, value: st.id }))}
                                label={i18next.t('offertemplate-service-type')}
                                value={serviceTypeId}
                                disabled={props.disabled || isServiceTypeChangeDialogOpen}
                              />
                            </Grid>
                          </Grid>
                        </SimpleAccordion>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={showHtml ? 12 : 4}>
                      {/* Input Summary */}
                      {showHtml && <SimpleAccordion header={`${i18next.t('offertemplate-section-input')}`}>{_inputSummary()}</SimpleAccordion>}
                      {!showHtml && (
                        <LabeledFrame variant="h6" label={`${i18next.t('offertemplate-section-input')}`}>
                          {_inputSummary()}
                        </LabeledFrame>
                      )}

                      {(props.data.hotelMessage || (props.data.requestFiles?.length ?? 0) > 0) && <Box mt={2} />}
                      {(props.data.hotelMessage || (props.data.requestFiles?.length ?? 0) > 0) && (
                        <>
                          {showHtml && (
                            <SimpleAccordion
                              header={i18next.t('customer-message-for-hotel') || 'Customer Message'}
                              icon={<ErrorIcon sx={{ color: '#ac2a6e' }} />}
                            >
                              <Box
                                sx={
                                  props.highlightChangeRequestItems
                                    ? { backgroundColor: 'rgba(245, 158, 11, 0.08)', borderRadius: 1, p: 1.5 }
                                    : undefined
                                }
                              >
                                {props.data.hotelMessage && (
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {props.data.hotelMessage}
                                  </Typography>
                                )}
                                <BookingRequestFiles files={props.data.requestFiles ?? []} />
                                <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" mt={1}>
                                  {i18next.t('customer-message-readonly')}
                                </Typography>
                              </Box>
                            </SimpleAccordion>
                          )}
                          {!showHtml && (
                            <LabeledFrame
                              variant="h6"
                              label={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <ErrorIcon sx={{ color: '#ac2a6e', fontSize: '1.2rem' }} />
                                  <span>{i18next.t('customer-message-for-hotel') || 'Customer Message'}</span>
                                </Box>
                              }
                            >
                              <Box
                                sx={
                                  props.highlightChangeRequestItems
                                    ? { backgroundColor: 'rgba(245, 158, 11, 0.08)', borderRadius: 1, p: 1.5 }
                                    : undefined
                                }
                              >
                                {props.data.hotelMessage && (
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {props.data.hotelMessage}
                                  </Typography>
                                )}
                                <BookingRequestFiles files={props.data.requestFiles ?? []} />
                                <Typography variant="caption" color="text.secondary" fontStyle="italic" display="block" mt={1}>
                                  {i18next.t('customer-message-readonly')}
                                </Typography>
                              </Box>
                            </LabeledFrame>
                          )}
                        </>
                      )}
                    </Grid>
                    {advanced && (
                      <Grid item xs={12}>
                        <FormInputText
                          textFieldProps={{
                            multiline: true,
                            rows: 4,
                          }}
                          name="version"
                          control={control}
                          label={i18next.t('offertemplate-version')}
                          required
                          disabled={props.disabled}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <SimpleAccordion
                        header={`${i18next.t('offertemplate-section-client')}`}
                        icon={<>{!props.disabled && props.canChangeClientData && hintValidationAlert(validationErrors.client)}</>}
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={fullWidth ? 4 : 12}>
                            <FormInputDate
                              name="offerDate"
                              control={control}
                              label={i18next.t('offertemplate-offerdate')}
                              required
                              disabled={props.disabled}
                            />
                          </Grid>
                          {props.offerSource === EOfferSource.WIDGET && (
                            <Grid item xs={12} sm={fullWidth ? 4 : 12}>
                              <FormInputDate
                                name="startDate"
                                control={control}
                                label={i18next.t('offertemplate-startdate')}
                                required
                                disabled={props.disabled}
                                calendarDateString
                              />
                            </Grid>
                          )}
                          {props.canSeeClientData && (
                            <>
                              <Grid item xs={12} sm={fullWidth ? 4 : 12}>
                                <FormInputText
                                  name="client.email"
                                  control={control}
                                  label={i18next.t('offertemplate-client-email')}
                                  required
                                  disabled={props.disabled || !props.canChangeClientData}
                                />
                              </Grid>
                              <Grid item xs={12} sm={fullWidth ? 4 : 12}>
                                <FormInputText
                                  name="client.phone"
                                  control={control}
                                  label={i18next.t('offertemplate-client-phone')}
                                  required={false}
                                  disabled={props.disabled || !props.canChangeClientData}
                                />
                              </Grid>
                              <Grid item xs={12} />
                              <Grid item xs={12} sm={fullWidth ? 6 : 12}>
                                <LabeledFrame label={`${i18next.t('offertemplate-section-client-company')}`}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                      <FormInputText
                                        name="client.company"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-company')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.firstname"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-firstname')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.lastname"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-lastname')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <FormInputText
                                        name="client.address"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-address')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.zip"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-zip')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.city"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-city')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputCountryDropdown
                                        name="client.country"
                                        control={control}
                                        label={i18next.t('offertemplate-client-company-country')}
                                        required
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                  </Grid>
                                </LabeledFrame>
                              </Grid>
                              <Grid item xs={12} sm={fullWidth ? 6 : 12}>
                                <LabeledFrame label={`${i18next.t('offertemplate-section-client-billing')}`}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                      <FormInputText
                                        name="client.billingCompany"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-company')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.billingFirstname"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-firstname')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.billingLastname"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-lastname')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <FormInputText
                                        name="client.billingAddress"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-address')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.billingZip"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-zip')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputText
                                        name="client.billingCity"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-city')}
                                        required={clientAddressRequired}
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormInputCountryDropdown
                                        name="client.billingCountry"
                                        control={control}
                                        label={i18next.t('offertemplate-client-billing-country')}
                                        required
                                        disabled={props.disabled || !props.canChangeClientData}
                                      />
                                    </Grid>
                                  </Grid>
                                </LabeledFrame>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={12}>
                            <FormInputText
                              textFieldProps={{
                                multiline: true,
                                rows: 4,
                              }}
                              name="additionalInfo1"
                              control={control}
                              label={i18next.t('offertemplate-additionalinfo1')}
                              disabled={props.disabled}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormInputText
                              textFieldProps={{
                                multiline: true,
                                rows: 4,
                              }}
                              name="additionalInfo2"
                              control={control}
                              label={i18next.t('offertemplate-additionalinfo2')}
                              disabled={props.disabled}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormInputText
                              textFieldProps={{
                                multiline: true,
                                rows: 4,
                              }}
                              name="additionalInfo3"
                              control={control}
                              label={i18next.t('offertemplate-additionalinfo3')}
                              disabled={props.disabled}
                            />
                          </Grid>
                        </Grid>
                      </SimpleAccordion>
                    </Grid>
                    {advanced && (
                      <>
                        <Grid item xs={12}>
                          <SimpleAccordion header={`${i18next.t('offertemplate-section-document')}`}>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <FormInputText name="title" control={control} label={i18next.t('offertemplate-title')} disabled={!props.disabled} />
                              </Grid>
                              <Grid item xs={12}>
                                <FormInputText
                                  name="filename"
                                  control={control}
                                  label={i18next.t('offertemplate-filename')}
                                  disabled={!props.disabled}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <FormInputCheckbox
                                  name="includeEmptyLineItems"
                                  control={control}
                                  label={i18next.t('offertemplate-includeemptylineitems')}
                                  disabled={!props.disabled}
                                />
                              </Grid>
                            </Grid>
                          </SimpleAccordion>
                        </Grid>
                        <Grid item xs={12}>
                          <SimpleAccordion header={`${i18next.t('offertemplate-section-header')}`}>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <FormInputText
                                  name="pageOptions.headerTemplate"
                                  label={i18next.t('offertemplate-header')}
                                  textFieldProps={{
                                    multiline: true,
                                    rows: 10,
                                  }}
                                  control={control}
                                  disabled={!props.disabled}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <FormInputText
                                  name="pageOptions.footerTemplate"
                                  label={i18next.t('offertemplate-footer')}
                                  textFieldProps={{
                                    multiline: true,
                                    rows: 10,
                                  }}
                                  control={control}
                                  disabled={!props.disabled}
                                />
                              </Grid>
                            </Grid>
                          </SimpleAccordion>
                        </Grid>
                        <Grid item xs={12}>
                          <SimpleAccordion
                            header={`${i18next.t('offertemplate-section-page')}`}
                            icon={<>{hintValidationAlert(validationErrors.pageOptions)}</>}
                          >
                            <Grid container spacing={3}>
                              <Grid item xs={6}>
                                <FormInputText
                                  name="pageOptions.marginTop"
                                  label={i18next.t('offertemplate-marginTop')}
                                  control={control}
                                  required
                                  disabled={!props.disabled}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <FormInputText
                                  name="pageOptions.marginBottom"
                                  label={i18next.t('offertemplate-marginBottom')}
                                  control={control}
                                  required
                                  disabled={!props.disabled}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <FormInputText
                                  name="pageOptions.marginLeft"
                                  label={i18next.t('offertemplate-marginLeft')}
                                  control={control}
                                  required
                                  disabled={!props.disabled}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <FormInputText
                                  name="pageOptions.marginRight"
                                  label={i18next.t('offertemplate-marginRight')}
                                  control={control}
                                  required
                                  disabled={!props.disabled}
                                />
                              </Grid>
                            </Grid>
                          </SimpleAccordion>
                        </Grid>
                        <Grid item xs={12}>
                          <SimpleAccordion header={`${i18next.t('offertemplate-section-css')}`}>
                            <FormInputText
                              name="pageOptions.cssStyles"
                              label={i18next.t('offertemplate-css')}
                              textFieldProps={{
                                multiline: true,
                                rows: 10,
                              }}
                              control={control}
                              disabled={!props.disabled}
                            />
                          </SimpleAccordion>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                        <Grid item xs={12}>
                          <DragDropContext
                            onDragEnd={result => {
                              if (!result.destination) return;

                              const contentBlocks = [...getValues('contentBlocks')];
                              const visibleIndexes = visibleContentBlocks.map(({ index }) => index);
                              const visibleBlocks = visibleIndexes.map(index => contentBlocks[index]);
                              const [reorderedItem] = visibleBlocks.splice(result.source.index, 1);
                              if (!reorderedItem) return;
                              visibleBlocks.splice(result.destination.index, 0, reorderedItem);

                              visibleIndexes.forEach((index, visibleIndex) => {
                                contentBlocks[index] = visibleBlocks[visibleIndex];
                              });

                              _reassignSequence(contentBlocks);
                            }}
                          >
                            <Droppable droppableId="characters">
                              {(provided: any) => (
                                <Grid container spacing={3} {...provided.droppableProps} ref={provided.innerRef} className="characters">
                                  {visibleContentBlocks.map(({ cb, index }, visibleIndex) => (
                                    <Draggable
                                      key={`${cb.id}`}
                                      draggableId={`content-block-${cb.id}`}
                                      index={visibleIndex}
                                      isDragDisabled={
                                        props.disabled || !props.canChangeStructure || cb.type === 'LINE_ITEMS' || cb.type === 'CANCELLATION_ITEMS'
                                      }
                                    >
                                      {(provided: any) => (
                                        <Grid item xs={12} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                          <EditorAccordion
                                            cssStyles={watch('pageOptions.cssStyles') || ''}
                                            contentBlock={cb}
                                            showHtmlTab={true}
                                            fullWidth={fullWidth}
                                            contentBlockIndex={index}
                                            control={control}
                                            canChangeLineItemCount={canChangeLineItemCount}
                                            canChangeLineItemPrice={canChangeLineItemPrice}
                                            canChangeLineItemHeader={canChangeLineItemHeader}
                                            canDeleteLineItem={canDeleteLineItem}
                                            showLineItem={showLineItem}
                                            isLineItemHighlighted={isLineItemHighlighted}
                                            onDelete={onContentBlockDelete}
                                            onTotalsChanged={onTotalsChanged}
                                            onLineItemChanged={onLineItemChanged}
                                            onLineItemCopyPrices={onLineItemCopyPrices}
                                            onLineItemPriceComponentChanged={onLineItemPriceComponentChanged}
                                            onLineItemPriceItemManuallyChanged={onLineItemPriceItemManuallyChanged}
                                            onLineItemSKUAdded={onLineItemSKUAdded}
                                            onLineItemFreeAdded={onLineItemFreeAdded}
                                            disabled={props.disabled}
                                            canChangeCounts={props.canChangeCounts}
                                            canChangePrices={props.canChangePrices}
                                            canChangeStructure={props.canChangeStructure}
                                            canChangeItems={props.canChangeItems}
                                            canAddItems={props.canAddItems}
                                            canDeleteItems={props.canDeleteItems}
                                            isPricesNet={isPricesNet}
                                            priceListId={props.data.priceList.id}
                                            guestCount={maxGuests || 1}
                                            isPremium={props.data.offer.publicCompany?.isPremium ?? false}
                                            publicCompanyId={props.data.offer.publicCompany?.id ?? undefined}
                                            hotelMessage={props.offerSource === EOfferSource.WIDGET ? props.data.input?.comment : props.data.hotelMessage}
                                            spaceId={props.data.space.id}
                                            roomRequests={props.data.inputLister?.days}
                                          />
                                        </Grid>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </Grid>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </Grid>
                      </>
                    )}
                  </>}
                  {!advanced &&
                    contentBlocksFields.map((cb, index) => {
                      if (cb.type === 'LINE_ITEMS' || cb.type === 'CANCELLATION_ITEMS') {
                        return shouldShowContentBlock(index) && (
                          <Grid item xs={12} key={`${cb.id}_${cb.sequence}_s`}>
                            <EditorAccordion
                              cssStyles={watch('pageOptions.cssStyles') || ''}
                              showHtmlTab={false}
                              fullWidth={fullWidth}
                              contentBlock={cb}
                              contentBlockIndex={index}
                              control={control}
                              canChangeLineItemCount={canChangeLineItemCount}
                              canChangeLineItemPrice={canChangeLineItemPrice}
                              canChangeLineItemHeader={canChangeLineItemHeader}
                              canDeleteLineItem={canDeleteLineItem}
                              showLineItem={showLineItem}
                              isLineItemHighlighted={isLineItemHighlighted}
                              onDelete={onContentBlockDelete}
                              onTotalsChanged={onTotalsChanged}
                              onLineItemChanged={onLineItemChanged}
                              onLineItemCopyPrices={onLineItemCopyPrices}
                              onLineItemPriceComponentChanged={onLineItemPriceComponentChanged}
                              onLineItemPriceItemManuallyChanged={onLineItemPriceItemManuallyChanged}
                              onLineItemSKUAdded={onLineItemSKUAdded}
                              onLineItemFreeAdded={onLineItemFreeAdded}
                              disabled={props.disabled}
                              canChangeCounts={props.canChangeCounts}
                              canChangePrices={props.canChangePrices}
                              canChangeStructure={props.canChangeStructure}
                              canChangeItems={props.canChangeItems}
                              canAddItems={props.canAddItems}
                              canDeleteItems={props.canDeleteItems}
                              isPricesNet={isPricesNet}
                              priceListId={props.data.priceList.id}
                              guestCount={maxGuests || 1}
                              isPremium={props.data.offer.publicCompany?.isPremium ?? false}
                              publicCompanyId={props.data.offer.publicCompany?.id ?? undefined}
                              hotelMessage={props.offerSource === EOfferSource.WIDGET ? props.data.input?.comment : props.data.hotelMessage}
                              spaceId={props.data.space.id}
                              roomRequests={props.data.inputLister?.days}
                            />
                          </Grid>
                        );
                      }
                      return null;
                    })}
                  {!showChangeRequest && (
                    <>
                      <Grid item xs={12} sm={props.offerSource === EOfferSource.LISTER ? 6 : 12}>
                        {showHtml && (
                          <SimpleAccordion header={`${i18next.t('offertemplate-section-totals')}`} autoOpen>
                            {_priceSummary()}
                          </SimpleAccordion>
                        )}
                        {!showHtml && (
                          <LabeledFrame variant="h6" label={i18next.t('offertemplate-section-totals')}>
                            {_priceSummary()}
                          </LabeledFrame>
                        )}
                      </Grid>
                      {props.offerSource === EOfferSource.LISTER && (
                        <Grid item xs={12} sm={6}>
                          <LabeledFrame variant="h6" label={i18next.t('offer-personal-message')}>
                            <FormInputText
                              textFieldProps={{
                                multiline: true,
                                rows: 4,
                              }}
                              name="personalMessage"
                              control={control}
                              label=""
                              disabled={props.disabled}
                            />
                          </LabeledFrame>
                        </Grid>
                      )}
                    </>
                  )}
                  {!props.disabled && props.canChangeStructure && (
                    <Grid item xs={12}>
                      <Button
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        color="secondary"
                        startIcon={<PlusIcon />}
                        disabled={
                          isSubmitting ||
                          isValidating ||
                          !contentBlocksFields.find(c => c.type === 'LINE_ITEMS') ||
                          !!contentBlocksFields.find(c => c.type === 'LINE_ITEMS' && c.day === -1)
                        }
                        onClick={async () => {
                          const firstLineItemBlockIndex = contentBlocksFields.findIndex(c => c.type === 'LINE_ITEMS');
                          if (firstLineItemBlockIndex >= 0) {
                            const contentBlocks = [...getValues('contentBlocks')];
                            contentBlocks.splice(firstLineItemBlockIndex, 0, {
                              day: -1,
                              type: EOfferVersionContentBlockType.LINE_ITEMS,
                              sequence: -1,
                              template: contentBlocksFields[firstLineItemBlockIndex].template,
                              lineItems: [],
                              formFields: [],
                              cancellationRule: null,
                            });
                            _reassignSequence(contentBlocks);
                          }
                        }}
                      >
                        {i18next.t('offertemplate-add-previousday')}
                      </Button>
                      <Button
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        color="secondary"
                        startIcon={<PlusIcon />}
                        disabled={isSubmitting || isValidating || !contentBlocksFields.find(c => c.type === 'LINE_ITEMS')}
                        onClick={async () => {
                          const lastLineItemBlockIndex = contentBlocksFields.map(c => c.type === 'LINE_ITEMS').lastIndexOf(true);
                          if (lastLineItemBlockIndex >= 0) {
                            const contentBlocks = [...getValues('contentBlocks')];
                            contentBlocks.splice(lastLineItemBlockIndex + 1, 0, {
                              day: contentBlocksFields[lastLineItemBlockIndex].day! + 1,
                              type: EOfferVersionContentBlockType.LINE_ITEMS,
                              sequence: -1,
                              template: contentBlocksFields[lastLineItemBlockIndex].template,
                              lineItems: [],
                              formFields: [],
                              cancellationRule: null,
                            });
                            _reassignSequence(contentBlocks);
                          }
                        }}
                      >
                        {i18next.t('offertemplate-add-day')}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <OfferStandardDocuments
                      offerId={props.data.offer.id}
                      canEdit={!props.disabled && (props.canChangeItems || props.canAddItems)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <OfferDocuments
                      offerVersion={props.data}
                      canEdit={!props.disabled && (props.canChangeItems || props.canAddItems)}
                      guestMode={props.guestMode}
                      onDocumentChange={handleDocumentChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
      {isCancellationDialogOpen && (
        <CancellationRulesDialog
          open={isCancellationDialogOpen}
          onCancel={() => {
            setIsCancellationDialogOpen(false);
          }}
          onSave={async (rules, toleranceRate) => {
            await updateCancellationRules({
              variables: {
                offerVersionId: props.data.id,
                rules: rules,
                cancellationToleranceRate: toleranceRate
              },
              awaitRefetchQueries: true,
              // Do not refetch OFFERVERSION_VIEW_QUERY here: it updates `props.data` and triggers
              // `reset(toFormSchema(props.data))`, which wipes unsaved price edits in the form.
              refetchQueries: [{ query: OFFERVERSION_VIEW_CANCELLATION_RULES_QUERY, variables: { id: props.versionId } }],
            });
            setCancellationRulesBackup(rules);
            setIsCancellationDialogOpen(false);
          }}
          initialRules={JSON.parse(JSON.stringify(effectiveCancellationRules))}
          initialToleranceRate={effectiveCancellationToleranceRate ?? null}
          hotelHasCategoryCancellation={hotelHasCategoryCancellation}
        />
      )}
      {isDownpaymentDialogOpen && (
        <DownpaymentDialog
          open={isDownpaymentDialogOpen}
          onCancel={() => setIsDownpaymentDialogOpen(false)}
          onSave={async (downpayment) => {
            await updateDownpayment({
              variables: {
                offerVersionId: props.data.id,
                ...downpayment
              },
              awaitRefetchQueries: true,
              refetchQueries: [
                { query: OFFERVERSION_VIEW_DOWNPAYMENT_QUERY, variables: { id: props.versionId } },
              ],
            });
            setDownpaymentBackup(downpayment);
            setIsDownpaymentDialogOpen(false);
          }}
          initialDownpayment={downpaymentBackup ? JSON.parse(JSON.stringify(downpaymentBackup)) : undefined}
          guests={maxGuests}
          rooms={maxRoomsEZ + (maxRoomsDZ / 2)}
          totalPrice={watch('totalPriceGross')}
          currency={props.data.priceList.currency}
        />
      )}
      {isCommissionDialogOpen && (
        <CommissionDialog
          open={isCommissionDialogOpen}
          onCancel={() => setIsCommissionDialogOpen(false)}
          onSave={async (commission) => {
            // Persist only; do not refetch OFFERVERSION_VIEW_QUERY (would reset form and wipe unsaved price edits)
            await updateCommission({
              variables: {
                offerVersionId: props.data.id,
                commissionBase: commission.commissionBase as EOfferCommissionBase,
                commissionPercentage: commission.commissionPercentage,
              },
            });
            setCommissionRate(commission);
            setIsCommissionDialogOpen(false);
          }}
          initialCommission={commissionRate}
        />
      )}
      <ConfirmationDialog
        open={isServiceTypeChangeDialogOpen}
        setOpen={setIsServiceTypeChangeDialogOpen}
        title={String(i18next.t('offer-editor-change-servicetype-title'))}
        onConfirm={_onServiceTypeChangeConfirmed}
        confirmButtonText={String(i18next.t('confirm'))}
        declineButtonText={String(i18next.t('cancel'))}
      >
        {i18next.t('offertemplate-service-type-change-confirmation')}
      </ConfirmationDialog>
    </QuickPriceCalculatorContext.Provider>
  );
}

/**
 * Main offer version editor component.
 * Handles offer editing, approval/rejection, cancellation rules, downpayments, and document management.
 * Uses template editor for content blocks and quick price calculator for pricing.
 */
export default function OfferVersion(props: OfferVersionProps) {
  const offerVersionQuery = useQuery(OFFERVERSION_VIEW_QUERY, {
    variables: { id: props.versionId },
  });
  const offerVersionDownpaymentQuery = useQuery(OFFERVERSION_VIEW_DOWNPAYMENT_QUERY, {
    variables: { id: props.versionId },
  });
  const offerVersionCancellationRulesQuery = useQuery(OFFERVERSION_VIEW_CANCELLATION_RULES_QUERY, {
    variables: { id: props.versionId },
  });
  const offerVersionQuickPriceQuery = useQuery(OFFERVERSION_QUICKPRICE_QUERY, {
    variables: { id: props.versionId },
    fetchPolicy: 'network-only',
  });
  const offerVersionProductTextQuery = useQuery(OFFERVERSION_PRODUCTTEXT_QUERY, {
    variables: { id: props.versionId },
  });
  const taxTypesQuery = useQuery(TAXTYPES_LIST_FULL_QUERY);
  const serviceTypesQuery = useQuery(SERVICETYPES_LIST_TEXT_QUERY, {
    variables: {
      spaceId: offerVersionQuery.data?.viewWlOfferVersion?.hotel?.spaceId || 0
    },
    skip: !offerVersionQuery.data?.viewWlOfferVersion
  });

  const findOfferTemplateQuery = useQuery(FIND_OFFERTEMPLATE_QUERY, {
    variables: {
      hotelId: offerVersionQuery.data?.viewWlOfferVersion?.hotel?.id || 0,
      source: offerVersionQuery.data?.viewWlOfferVersion?.offer.source || EOfferSource.WIDGET,
      type: props.applyOfferTemplateType || EContentOfferTemplateType.OFFER
    },
    skip: !props.applyOfferTemplateType || !offerVersionQuery.data?.viewWlOfferVersion
  });

  const loading =
    offerVersionQuickPriceQuery.loading ||
    offerVersionDownpaymentQuery.loading ||
    offerVersionCancellationRulesQuery.loading ||
    offerVersionProductTextQuery.loading ||
    offerVersionQuery.loading ||
    taxTypesQuery.loading ||
    serviceTypesQuery.loading ||
    (props.applyOfferTemplateType && findOfferTemplateQuery.loading);
  const error = offerVersionQuickPriceQuery.error || offerVersionDownpaymentQuery.error || offerVersionCancellationRulesQuery.error || offerVersionProductTextQuery.error || offerVersionQuery.error || taxTypesQuery.error || serviceTypesQuery.error || findOfferTemplateQuery.error;

  const versionData = offerVersionQuery.data?.viewWlOfferVersion;
  const quickPriceDehydrate = offerVersionQuickPriceQuery.data?.offerVersionQuickPriceDehydrate;
  const downpaymentData = offerVersionDownpaymentQuery.data?.viewWlOfferVersion;
  const cancellationRulesData = offerVersionCancellationRulesQuery.data?.viewWlOfferVersion;
  const taxTypes = taxTypesQuery.data?.listTaxTypes;
  const serviceTypes = serviceTypesQuery.data?.listServiceTypes?.serviceTypes;

  const calc = useMemo(() => {
    const instance = props.offerSource === 'WIDGET' ? new QuickPriceCalculatorWidget() : new QuickPriceCalculatorLister();
    if (quickPriceDehydrate) {
      instance.hydrate(quickPriceDehydrate);
    }
    return instance;
  }, [props.offerSource, quickPriceDehydrate]);

  if (error) return <RedirectError err={error} />;

  if (
    loading ||
    !versionData ||
    !quickPriceDehydrate ||
    !downpaymentData ||
    !cancellationRulesData ||
    !taxTypes ||
    !serviceTypes
  ) {
    return <CircularProgress />;
  }

  return (
    <OfferVersionForm
      quickPriceCalculator={calc}
      downpaymentData={downpaymentData}
      cancellationRulesData={cancellationRulesData}
      taxTypes={taxTypes}
      serviceTypes={serviceTypes}
      applyOfferTemplate={findOfferTemplateQuery.data?.findOfferTemplate}
      data={versionData}
      {...props}
    />
  );
}

